import React from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { Lightbulb, MessageCircle, Rocket, Handshake } from 'lucide-react';

const features = [
  {
    icon: Lightbulb,
    title: 'Inspiring Keynotes',
    description: 'Learn from successful women leaders and industry experts shaping the future of business.'
  },
  {
    icon: MessageCircle,
    title: 'Interactive Discussions',
    description: 'Share insights, exchange ideas, and solve real business challenges together.'
  },
  {
    icon: Rocket,
    title: 'Pitch & Showcase',
    description: 'Pitch your business, get visibility, and access new opportunities for growth.'
  },
  {
    icon: Handshake,
    title: 'Business Matching',
    description: 'Connect with investors, partners, and potential collaborators from 10+ countries.'
  }
];

export const WomenEntrepreneursSection = () => {
  const { settings } = useSettings();

  if (settings.showWomenEntrepreneurs === false) return null;

  return (
    <section id="women-entrepreneurs" className="relative overflow-hidden" data-testid="women-entrepreneurs-section">
      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-[#7b2d8e] via-[#9b3fb5] to-[#c74ec9] py-16 md:py-20">
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white/90 px-4 py-1.5 rounded-full text-sm font-medium mb-6 border border-white/20">
            September 22-24, 2026 &middot; Batam, Indonesia
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Women Entrepreneur Summit
          </h2>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-6">
            An International Movement Uniting Women Entrepreneurs Across 10+ Countries
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-white/90 text-sm font-medium mb-8">
            <span className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">EMPOWER</span>
            <span className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">INNOVATE</span>
            <span className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">INSPIRE</span>
          </div>
          <div className="flex flex-wrap justify-center gap-8 md:gap-12">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">300+</div>
              <div className="text-sm text-white/70 mt-1">Women Entrepreneurs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">20+</div>
              <div className="text-sm text-white/70 mt-1">Speakers & Mentors</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">10+</div>
              <div className="text-sm text-white/70 mt-1">Countries</div>
            </div>
          </div>
        </div>
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4" />
      </div>

      {/* What to Expect */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
            What to Expect
          </h3>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            3 Days. Meaningful Connections. Real Business Growth.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 text-center group"
                  data-testid={`we-feature-${i}`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-purple-50 rounded-xl flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                    <Icon size={26} className="text-[#9b3fb5]" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
